/**
 * Block-type contract — the SINGLE source of truth for the atom vocabulary.
 *
 * Types AND arrays are both derived from the maps below, so the union type and
 * the runtime list can never drift apart (the internal fork the old two-list
 * setup risked). Adding an atom = one edit here; the type, the registry array,
 * and the validator all follow, and the drift-guard test enforces the invariants.
 *
 * On the ecosystem SoT: gemtheme's `t9-section-runtime-contract` validates the
 * Shopify section *runtime* (gem-section roots, container contracts, seeded
 * blocks) — it is NOT a flat list of render atoms in SurfaceSpec's shape, and
 * the section catalog has no single registry yet (known drift). So this map is
 * gemsales' pinned view of the shared vocabulary: each `shared` atom carries its
 * ecosystem `gem-*` section counterpart. Changing an entry is a deliberate
 * reconciliation with that catalog — not something to fork silently.
 */

/** Shared atoms — each maps to its ecosystem section-runtime `gem-*` id. */
export const SECTION_RUNTIME_ATOMS = {
  hero: "gem-hero",
  product: "gem-product",
  comparison: "gem-comparison",
  proof: "gem-lab-proof",
  reviews: "gem-reviews",
  faq: "gem-faq",
  bundle: "gem-bundle",
  metric: "gem-metric",
  timeline: "gem-timeline",
  summary: "gem-summary",
  cta: "gem-cta",
} as const;

/** gemsales-only sales/story atoms the theme section catalog does not own. */
export const GEMSALES_ATOMS = {
  answer: "gemsales-answer",
  hook: "gemsales-hook",
  agitate: "gemsales-agitate",
  urgency: "gemsales-urgency",
  guarantee: "gemsales-guarantee",
} as const;

export type SharedBlockType = keyof typeof SECTION_RUNTIME_ATOMS;
export type GemSalesBlockType = keyof typeof GEMSALES_ATOMS;
export type BlockType = SharedBlockType | GemSalesBlockType;

export const SHARED_BLOCK_TYPES: readonly SharedBlockType[] = Object.keys(SECTION_RUNTIME_ATOMS) as SharedBlockType[];
export const GEMSALES_BLOCK_TYPES: readonly GemSalesBlockType[] = Object.keys(GEMSALES_ATOMS) as GemSalesBlockType[];

/** The ecosystem section-runtime id an atom pins to (diagnostics / reconciliation). */
export function ecosystemSectionId(type: BlockType): string {
  return type in SECTION_RUNTIME_ATOMS
    ? SECTION_RUNTIME_ATOMS[type as SharedBlockType]
    : GEMSALES_ATOMS[type as GemSalesBlockType];
}
